import { memo, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import RichTextEditor from '@/components/RichTextEditor';
import api from '@/lib/api';

/**
 * Meeting-note edit form, extracted from ProjectDetailPanel so keystroke
 * updates only re-render this small subtree. Same rationale as
 * MeetingNoteCreateForm.
 */
function MeetingNoteEditForm({ projectId, note, onSaved, onCancel }) {
  const [form, setForm] = useState({
    date: note.date || '',
    attendees: note.attendees || '',
    notes: note.notes || '',
  });

  const setDate = useCallback((e) => setForm(f => ({ ...f, date: e.target.value })), []);
  const setAttendees = useCallback((e) => setForm(f => ({ ...f, attendees: e.target.value })), []);
  const setNotes = useCallback((v) => setForm(f => ({ ...f, notes: v })), []);

  const save = async () => {
    if (!form.notes.trim()) return toast.error('Notes required');
    await api.put(`/projects/${projectId}/meeting-notes/${note.id}`, form);
    toast.success('Meeting note updated');
    onSaved?.();
  };

  return (
    <Card className="border-primary/30 border-dashed bg-primary/5 shadow-none">
      <CardContent className="p-3 space-y-2">
        <p className="text-xs font-semibold text-primary">Edit Meeting Note</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={setDate}
              className="bg-background border-border"
              data-testid="edit-meeting-note-date"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Attendees</Label>
            <Input
              placeholder="e.g. John, Sarah, Mike"
              value={form.attendees}
              onChange={setAttendees}
              className="bg-background border-border"
              data-testid="edit-meeting-note-attendees"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Notes *</Label>
          <RichTextEditor
            placeholder="Meeting notes..."
            value={form.notes}
            onChange={setNotes}
            data-testid="edit-meeting-note-content"
          />
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={save}
            className="bg-primary text-primary-foreground"
            data-testid="update-meeting-note-btn"
          >
            Save Changes
          </Button>
          <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default memo(MeetingNoteEditForm);
